import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WebSocketContext from '../contexts/WebSocketContext';
import { BattleRoomBase } from '../components/battle-room-v2/components/BattleRoom';
import { ENTITLEMENTS_CACHE_KEY, buildBattleRoomHarnessEntitlementsSnapshot } from './battleRoomHarnessFixtures';
import { USER_TIERS } from '../config/entitlements';
import { buildGmaBattleRoomSession, createGmaMapMutationOutbox, verifyBattleRoomMapRuntime } from './gmaBattleRoomBridgeAdapter';
import GmaEncounterBriefing from './GmaEncounterBriefing';
import './GmaBattleRoomBridgePage.css';

export { buildGmaBattleRoomSession } from './gmaBattleRoomBridgeAdapter';

const GMA_BASE_URL = (process.env.REACT_APP_GMA_BASE_URL || 'http://127.0.0.1:4310').replace(/\/+$/, '');
const noop = () => {};

const bridgeShellStyle = { minHeight: '100vh', background: '#0d131d' };
const bridgeBarStyle = { height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '0 16px', borderBottom: '1px solid rgba(199,169,107,.28)', background: 'rgba(9,14,22,.97)', color: '#f3ead7' };
const bridgeBadgeStyle = { display: 'inline-flex', alignItems: 'center', gap: '7px', color: '#b9c9dc', fontSize: '12px' };
const bridgeButtonStyle = { border: '1px solid rgba(199,169,107,.34)', borderRadius: '8px', background: '#223147', color: '#f3ead7', padding: '7px 11px', fontSize: '12px', cursor: 'pointer' };

export default function GmaBattleRoomBridgePage() {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId') || '';
  const sessionId = searchParams.get('sessionId') || '';
  const hideEncounterBriefing = searchParams.get('briefing') === '0' || searchParams.get('hideBriefing') === '1';
  const autoNpcTurns = !['0', 'false', 'off'].includes(String(searchParams.get('autoNpc') || '1').toLowerCase());
  const [dashboard, setDashboard] = useState(null);
  const [encounterContextOverride, setEncounterContextOverride] = useState(null);
  const [error, setError] = useState('');
  const [lastSync, setLastSync] = useState(null);
  const [syncNotice, setSyncNotice] = useState('');
  const briefingStorageKey = `vcs:gma-encounter-briefing-collapsed:${sessionId || 'default'}`;
  const [briefingCollapsed, setBriefingCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(`vcs:gma-encounter-briefing-collapsed:${sessionId || 'default'}`) === '1';
    } catch (_) {
      return false;
    }
  });
  const [mapOutboxState, setMapOutboxState] = useState({ pendingCount: 0, halted: false, syncing: false, error: null });
  const lastGoodSyncRef = useRef(null);
  const npcTurnsInFlightRef = useRef(false);
  const initiativeSyncInFlightRef = useRef(false);
  const mapOutboxStateRef = useRef(mapOutboxState);
  const refreshAfterMutationRef = useRef(false);

  useEffect(() => {
    try {
      setBriefingCollapsed(window.localStorage.getItem(briefingStorageKey) === '1');
    } catch (_) {
      setBriefingCollapsed(false);
    }
  }, [briefingStorageKey]);

  const refresh = useCallback(async () => {
    if (!campaignId || !sessionId) return;
    if (mapOutboxStateRef.current.pendingCount > 0 || mapOutboxStateRef.current.syncing) {
      refreshAfterMutationRef.current = true;
      return;
    }
    try {
      const query = new URLSearchParams({ campaignId, sessionId, mechanicsOnly: '1' });
      const response = await fetch(`${GMA_BASE_URL}/api/gma/v1/interface/dashboard?${query}`);
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error?.message || `GMA returned ${response.status}`);
      setDashboard(payload);
      if (
        encounterContextOverride?.roomId
        && (payload?.battleRoom?.id !== encounterContextOverride.roomId || Date.now() - Number(encounterContextOverride.at || 0) > 60000)
      ) {
        setEncounterContextOverride(null);
      }
      const vcsSync = payload?.sync?.vcs;
      if (vcsSync?.status === 'current') {
        setLastSync(new Date(vcsSync.observedAt || Date.now()));
        lastGoodSyncRef.current = new Date(vcsSync.observedAt || Date.now());
        setSyncNotice(vcsSync.message || 'VCS is current.');
        setError('');
      } else {
        setSyncNotice(vcsSync?.message || 'Showing the last known good VCS state.');
        setError(vcsSync?.message || 'VCS refresh is unavailable.');
      }
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      const lastGood = lastGoodSyncRef.current;
      setError(lastGood ? `${message} Last known good sync: ${lastGood.toLocaleTimeString()}. No newer service snapshot was applied.` : message);
    }
  }, [campaignId, encounterContextOverride?.at, encounterContextOverride?.roomId, sessionId]);

  useEffect(() => {
    window.localStorage.setItem(ENTITLEMENTS_CACHE_KEY, JSON.stringify(buildBattleRoomHarnessEntitlementsSnapshot(USER_TIERS.PREMIUM)));
    refresh();
    const timer = window.setInterval(refresh, 1800);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const sessionData = useMemo(() => buildGmaBattleRoomSession(dashboard), [dashboard]);
  const roomName = dashboard?.battleRoom?.name || 'VCS BattleRoom';
  const activeEncounterContextOverride = (
    encounterContextOverride?.roomId
    && dashboard?.battleRoom?.id === encounterContextOverride.roomId
    && Date.now() - Number(encounterContextOverride.at || 0) <= 60000
  ) ? encounterContextOverride : null;
  const encounterContext = (
    activeEncounterContextOverride
      ? activeEncounterContextOverride.context
      : dashboard?.battleRoom?.encounterContext
  ) || {};
  const activeCombatant = dashboard?.battleRoom?.state?.combatants?.find((combatant) => String(combatant.id) === String(dashboard?.battleRoom?.state?.initiative?.activeCombatantId));
  const playerOptions = useMemo(() => (
    Array.isArray(encounterContext.playerOptions) && encounterContext.playerOptions.length
      ? encounterContext.playerOptions
      : [
        { label: 'Pursue the objective', description: 'Use your position, abilities, and surroundings to advance the immediate goal.' },
        { label: 'Change the situation', description: 'Move, hide, distract, prepare, or use the environment.' },
        { label: 'Avoid violence', description: 'Talk, deceive, withdraw, flee, or attempt another plausible solution.' },
      ]
  ), [encounterContext.playerOptions]);
  const sendMapMutation = useCallback(async (mutationMapState) => {
    let lastFailure = null;
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const response = await fetch(`${GMA_BASE_URL}/api/gma/v1/interface/battlerooms/${encodeURIComponent(sessionId)}/map`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mapState: mutationMapState }),
        });
        if (!response.ok) {
          const failedPayload = await response.json().catch(() => null);
          const failure = new Error(failedPayload?.error?.message || `GMA returned ${response.status}`);
          failure.code = failedPayload?.error?.code ?? null;
          failure.correlationId = failedPayload?.error?.correlationId ?? null;
          throw failure;
        }
        const payload = await response.json().catch(() => ({}));
        const verification = verifyBattleRoomMapRuntime(mutationMapState, payload?.battleRoom);
        if (!verification.ok) throw new Error(verification.reason);
        if (payload?.battleRoom) setDashboard((previous) => previous ? ({ ...previous, battleRoom: payload.battleRoom }) : previous);
        setLastSync(new Date());
        setError('');
        setSyncNotice(attempt > 1 ? 'VCS state synchronized after an automatic retry.' : 'VCS state synchronized and verified.');
        return payload;
      } catch (cause) {
        lastFailure = cause;
        if (attempt < 2) setSyncNotice('VCS state write was not confirmed; retrying once with the same mutation ID.');
      }
    }
    throw lastFailure || new Error('VCS state synchronization was not confirmed.');
  }, [sessionId]);

  const mapMutationOutbox = useMemo(() => {
    const storageKey = `gma:vcs-map-outbox:${sessionId}`;
    let initialMutations = [];
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) initialMutations = stored;
    } catch {
      window.localStorage.removeItem(storageKey);
    }
    return createGmaMapMutationOutbox({
      initialMutations,
      send: sendMapMutation,
      onPending: (pending) => {
        try {
          if (pending.length) window.localStorage.setItem(storageKey, JSON.stringify(pending));
          else window.localStorage.removeItem(storageKey);
        } catch (cause) {
          const reason = cause instanceof Error ? cause.message : String(cause);
          setError(`The pending VCS change is still held in this page, but the browser could not preserve it across reloads: ${reason}. Keep this page open and use Retry sync.`);
        }
      },
      onState: (nextState) => {
        mapOutboxStateRef.current = nextState;
        setMapOutboxState(nextState);
      },
    });
  }, [sendMapMutation, sessionId]);

  useEffect(() => {
    const restoredState = mapMutationOutbox.state();
    mapOutboxStateRef.current = restoredState;
    setMapOutboxState(restoredState);
    if (restoredState.pendingCount) {
      setError(`${restoredState.pendingCount} unconfirmed local VCS change${restoredState.pendingCount === 1 ? ' was' : 's were'} restored after reload. Retry sync to reconcile the same mutation IDs, or reload authoritative VCS state to discard them.`);
      setSyncNotice('VCS synchronization is paused with restored local changes pending.');
    }
  }, [mapMutationOutbox]);

  const updateMap = useCallback((mapState) => mapMutationOutbox.enqueue(mapState)
    .then(async (result) => {
      if (mapMutationOutbox.state().pendingCount === 0) {
        refreshAfterMutationRef.current = false;
        await refresh();
      }
      return result;
    })
    .catch((cause) => {
      const message = cause instanceof Error ? cause.message : String(cause);
      setError(`${message} The local change is queued and polling is paused. Retry sync or explicitly reload the authoritative VCS state.`);
      setSyncNotice('VCS synchronization is paused with a local change pending.');
      return false;
    }), [mapMutationOutbox, refresh]);

  const retryPendingMapMutations = useCallback(async () => {
    setSyncNotice(`Retrying ${mapMutationOutbox.state().pendingCount} queued VCS change${mapMutationOutbox.state().pendingCount === 1 ? '' : 's'}…`);
    try {
      await mapMutationOutbox.retry();
      refreshAfterMutationRef.current = false;
      await refresh();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      setError(`${message} The queued local changes are still preserved for another retry.`);
    }
  }, [mapMutationOutbox, refresh]);

  const discardPendingMapMutations = useCallback(async () => {
    const discarded = mapMutationOutbox.discard();
    setError('');
    setSyncNotice(`Discarded ${discarded} unconfirmed local change${discarded === 1 ? '' : 's'} and reloaded authoritative VCS state.`);
    refreshAfterMutationRef.current = false;
    await refresh();
  }, [mapMutationOutbox, refresh]);

  const postCombatLogEntry = useCallback((logEntry, messageType = 'combatLogEntry') => {
    if (!sessionId || !logEntry || typeof logEntry !== 'object') return false;
    fetch(`${GMA_BASE_URL}/api/gma/v1/interface/battlerooms/${encodeURIComponent(sessionId)}/combat-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaignId, messageType, logEntry }),
    }).then(async (response) => {
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error?.message || `GMA returned ${response.status}`);
      }
      const payload = await response.json().catch(() => ({}));
      const context = payload?.encounterContextUpdate?.encounterContext;
      if (context && payload?.battleRoom?.id) {
        setEncounterContextOverride({ roomId: payload.battleRoom.id, context, at: Date.now() });
        setDashboard((previous) => previous ? ({
          ...previous,
          battleRoom: {
            ...(previous.battleRoom || {}),
            ...(payload.battleRoom || {}),
            encounterContext: context,
          },
        }) : previous);
      } else if (payload?.battleRoom) {
        setDashboard((previous) => previous ? ({ ...previous, battleRoom: payload.battleRoom }) : previous);
      }
      setLastSync(new Date());
      setError('');
    }).catch((cause) => setError(cause instanceof Error ? cause.message : String(cause)));
    return true;
  }, [campaignId, sessionId]);

  const runNpcTurns = useCallback(async () => {
    if (!campaignId || !sessionId) return null;
    if (npcTurnsInFlightRef.current) return null;
    npcTurnsInFlightRef.current = true;
    try {
    const initiative = dashboard?.battleRoom?.state?.initiative ?? {};
    const turnIdentity = initiative.activeCombatantId
      ? `npc:${sessionId}:${initiative.round ?? 1}:${initiative.turn ?? 0}:${initiative.activeCombatantId}`
      : null;
    const response = await fetch(`${GMA_BASE_URL}/api/gma/v1/interface/encounters/${encodeURIComponent(sessionId)}/run-npc-turns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaignId, interactionId: turnIdentity }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error?.message || `GMA returned ${response.status}`);
    if (payload?.sync?.message) {
      setSyncNotice(payload.sync.message);
      if (payload.sync.lastKnownGood?.at) setLastSync(new Date(payload.sync.lastKnownGood.at));
    }
    return payload;
    } finally {
      npcTurnsInFlightRef.current = false;
    }
  }, [campaignId, dashboard?.battleRoom?.state?.initiative, sessionId]);

  const syncInitiativeUpdate = useCallback((initiativeData) => {
    if (!sessionId || !initiativeData) return true;
    if (initiativeSyncInFlightRef.current) return true;
    initiativeSyncInFlightRef.current = true;
    fetch(`${GMA_BASE_URL}/api/gma/v1/interface/battlerooms/${encodeURIComponent(sessionId)}/initiative`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ campaignId, initiativeData }),
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error?.message || `GMA returned ${response.status}`);
      const active = payload.activeCombatant;
      if (payload.changed && autoNpcTurns && active && !active.isPlayer) {
        await runNpcTurns();
      }
      await refresh();
    }).catch((cause) => setError(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => { initiativeSyncInFlightRef.current = false; });
    return true;
  }, [autoNpcTurns, campaignId, refresh, runNpcTurns, sessionId]);

  const sendBridgeWebSocketMessage = useCallback((messageType, payload) => {
    if (messageType === 'combatLogEntry' && payload?.logEntry) {
      if (String(payload.logEntry.type || '').toUpperCase() === 'TOKEN_MOVED') return true;
      return postCombatLogEntry(payload.logEntry, messageType);
    }
    if (messageType === 'combatAction' && payload) {
      return postCombatLogEntry({
        type: 'VCS_COMBAT_ACTION',
        round: payload.round ?? null,
        turn: payload.turn ?? null,
        combatantId: payload.actorId ?? payload.combatantId ?? null,
        data: payload,
        description: payload.description || payload.action?.name || 'VCS combat action',
        timestamp: new Date().toISOString(),
      }, messageType);
    }
    // Unsupported bridge messages must never report a successful mutation.
    // Token state is persisted only through sendMapUpdate's compact,
    // authoritative outbox path; claiming success here created split-brain UI.
    // date_of_change: 2026-07-18
    return false;
  }, [postCombatLogEntry]);

  const contextValue = useMemo(() => ({
    webSocketStatus: 'connected', isConnected: true, isConnecting: false, authError: null, isAuthLoaded: true,
    connect: async () => true, disconnect: noop, reconnect: async () => true,
    currentUser: { userId: 'gma-service', userName: 'GameMaster Assistant', token: null }, isSignedIn: true,
    userRole: 'gm', isGameMaster: true, currentSessionId: sessionId, sessionParticipants: [], sessionStatus: 'joined',
    joinSession: async () => ({ ok: true, role: 'gm' }), leaveSession: async () => true,
    combatState: null, lastUpdate: lastSync?.getTime() || 0,
    isInSession: true,
    sendCombatAction: (data) => sendBridgeWebSocketMessage('combatAction', data), sendInitiativeUpdate: syncInitiativeUpdate, sendTokenUpdate: () => false,
    sendMapUpdate: updateMap, sendCombatUpdate: () => false, sendWebSocketMessage: sendBridgeWebSocketMessage,
    addEventListener: () => noop,
    updateRole: noop,
    clearSessionState: noop,
    webSocketService: { ready: async () => true, on: noop, off: noop, emit: () => false, joinCombat: async () => ({ ok: true, role: 'gm' }), leaveCombat: noop },
  }), [lastSync, sessionId, sendBridgeWebSocketMessage, syncInitiativeUpdate, updateMap]);

  const renderBridgeTopbar = useCallback(() => (
    <div style={bridgeBarStyle}>
      <div style={bridgeBadgeStyle}><strong>VCS BattleRoom</strong><span>↔</span><strong style={{ color: '#d9b867' }}>GameMaster Assistant</strong></div>
      <strong>{roomName}</strong>
      <div style={bridgeBadgeStyle}>
        <span style={{ color: error || mapOutboxState.pendingCount ? '#e98b83' : '#74bb87' }}>●</span>
        <span title={syncNotice}>{error || (mapOutboxState.pendingCount
          ? `${mapOutboxState.pendingCount} local VCS change${mapOutboxState.pendingCount === 1 ? '' : 's'} awaiting confirmation`
          : (lastSync ? `VCS authoritative · synced ${lastSync.toLocaleTimeString()}` : 'Connecting…'))}</span>
        {mapOutboxState.pendingCount > 0
          ? <>
              <button type="button" style={bridgeButtonStyle} disabled={mapOutboxState.syncing} onClick={retryPendingMapMutations}>{mapOutboxState.syncing ? 'Retrying…' : `Retry sync (${mapOutboxState.pendingCount})`}</button>
              <button type="button" style={bridgeButtonStyle} disabled={mapOutboxState.syncing} onClick={discardPendingMapMutations} title="Discard unconfirmed local changes and reload the authoritative VCS snapshot">Reload VCS state</button>
            </>
          : <button type="button" style={bridgeButtonStyle} onClick={refresh}>Sync now</button>}
        <button type="button" style={bridgeButtonStyle} onClick={() => window.open(GMA_BASE_URL,'_blank')}>Open GMA ↗</button>
      </div>
    </div>
  ), [discardPendingMapMutations, error, lastSync, mapOutboxState, refresh, retryPendingMapMutations, roomName, syncNotice]);

  const toggleEncounterBriefing = useCallback(() => {
    setBriefingCollapsed((current) => {
      const next = !current;
      // Preserve the user's density preference for this BattleRoom without
      // making it part of authoritative encounter state. date_of_change: 2026-07-17
      try { window.localStorage.setItem(briefingStorageKey, next ? '1' : '0'); } catch (_) {}
      return next;
    });
  }, [briefingStorageKey]);

  if (!sessionData) return <div style={{ ...bridgeShellStyle, display: 'grid', placeItems: 'center', color: '#f3ead7' }}>{error || 'Connecting to the GMA BattleRoom…'}</div>;
  return <div style={bridgeShellStyle}><WebSocketContext.Provider value={contextValue}><BattleRoomBase sessionId={sessionId} activeCampaignId={campaignId} renderUserInfoTopbar={() => <>{renderBridgeTopbar()}{hideEncounterBriefing ? null : <GmaEncounterBriefing activeCombatant={activeCombatant} encounterContext={encounterContext} playerOptions={playerOptions} collapsed={briefingCollapsed} onToggle={toggleEncounterBriefing} onOpenGma={() => window.open(GMA_BASE_URL,'_blank')} />}</>} serviceSessionData={sessionData} /></WebSocketContext.Provider></div>;
}
